import React, { useEffect, useState } from 'react';

/**
 * CuteRobot — a single, charming grocery scout robot that drifts across the
 * viewport as the user scrolls. Rounded body, big smiling eyes, waving antenna.
 */

export const CuteRobot = ({ blink, wave }) => (
  <svg width="90" height="96" viewBox="0 0 90 96" fill="none">
    {/* Soft glow ring */}
    <ellipse cx="45" cy="82" rx="24" ry="4" fill="rgba(46,107,255,0.25)" />

    {/* Antenna */}
    <line x1="45" y1="10" x2="45" y2="20" stroke="rgba(255,255,255,0.8)" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="45" cy="8" r="3.4" fill="#2E6BFF">
      <animate attributeName="opacity" values="1;0.4;1" dur="1.8s" repeatCount="indefinite" />
    </circle>

    {/* Head - rounded rectangle */}
    <rect x="20" y="20" width="50" height="42" rx="14" fill="#f4f4f0" stroke="rgba(255,255,255,0.85)" strokeWidth="1.4" />

    {/* Cheek blush */}
    <ellipse cx="30" cy="46" rx="4" ry="2.4" fill="#ffb0c8" opacity="0.7" />
    <ellipse cx="60" cy="46" rx="4" ry="2.4" fill="#ffb0c8" opacity="0.7" />

    {/* Eyes (blink toggles closed) */}
    {blink ? (
      <>
        <path d="M32 38 Q35 41 38 38" stroke="#0b0b0e" strokeWidth="2" strokeLinecap="round" fill="none" />
        <path d="M52 38 Q55 41 58 38" stroke="#0b0b0e" strokeWidth="2" strokeLinecap="round" fill="none" />
      </>
    ) : (
      <>
        <circle cx="35" cy="38" r="3.6" fill="#0b0b0e" />
        <circle cx="55" cy="38" r="3.6" fill="#0b0b0e" />
        <circle cx="36.2" cy="36.6" r="1.1" fill="#ffffff" />
        <circle cx="56.2" cy="36.6" r="1.1" fill="#ffffff" />
      </>
    )}

    {/* Smile */}
    <path d="M38 50 Q45 56 52 50" stroke="#0b0b0e" strokeWidth="1.8" strokeLinecap="round" fill="none" />

    {/* Neck + tiny status LED */}
    <rect x="42" y="62" width="6" height="4" fill="rgba(255,255,255,0.5)" />
    <circle cx="45" cy="64" r="1" fill="#2E6BFF" />

    {/* Body */}
    <rect x="24" y="66" width="42" height="18" rx="8" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.6)" strokeWidth="1.2" />
    {/* screen */}
    <rect x="32" y="70" width="26" height="10" rx="2" fill="#0b0b0e" stroke="rgba(46,107,255,0.5)" />
    <text x="45" y="77.5" fontFamily="JetBrains Mono, monospace" fontSize="7" textAnchor="middle" fill="#7ba0ff">
      grocera
    </text>

    {/* Arms — one waving */}
    <g style={{ transformOrigin: '18px 72px', transform: wave ? 'rotate(-25deg)' : 'rotate(-10deg)', transition: 'transform 400ms ease' }}>
      <rect x="14" y="68" width="10" height="4" rx="2" fill="rgba(255,255,255,0.75)" />
      <circle cx="12" cy="70" r="3" fill="#f4f4f0" stroke="rgba(255,255,255,0.8)" strokeWidth="1" />
    </g>
    <g style={{ transformOrigin: '72px 72px', transform: 'rotate(10deg)' }}>
      <rect x="66" y="68" width="10" height="4" rx="2" fill="rgba(255,255,255,0.75)" />
      <circle cx="78" cy="70" r="3" fill="#f4f4f0" stroke="rgba(255,255,255,0.8)" strokeWidth="1" />
    </g>

    {/* Hover thrusters below */}
    <ellipse cx="34" cy="86" rx="4" ry="1.6" fill="rgba(46,107,255,0.6)">
      <animate attributeName="opacity" values="0.4;0.9;0.4" dur="0.8s" repeatCount="indefinite" />
    </ellipse>
    <ellipse cx="56" cy="86" rx="4" ry="1.6" fill="rgba(46,107,255,0.6)">
      <animate attributeName="opacity" values="0.9;0.4;0.9" dur="0.8s" repeatCount="indefinite" />
    </ellipse>
  </svg>
);

/**
 * Robot docks in the bottom-right corner and bobs gently as the user scrolls.
 * It used to weave across the whole viewport above everything (z-40), which
 * put it on top of hero copy and CTAs. Now it stays out of the content: it
 * appears only once the hero is scrolled past, and only at 1600px+ where
 * the gutter beside the 1400px content column keeps the corner clear. Also blinks every ~4s and waves periodically.
 */
export default function FlyingRobots() {
  const [scrollY, setScrollY] = useState(typeof window !== 'undefined' ? window.scrollY : 0);
  const [blink, setBlink] = useState(false);
  const [wave, setWave] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    // Blink loop
    const blinkId = setInterval(() => {
      setBlink(true);
      setTimeout(() => setBlink(false), 160);
    }, 4200);
    // Wave loop
    const waveId = setInterval(() => {
      setWave(true);
      setTimeout(() => setWave(false), 600);
    }, 5000);
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearInterval(blinkId);
      clearInterval(waveId);
    };
  }, []);

  const pastHero = scrollY > (typeof window !== 'undefined' ? window.innerHeight * 0.8 : 800);
  const bob = Math.sin(scrollY / 140) * 6;
  const tilt = Math.sin(scrollY / 260) * 5;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed bottom-6 right-6 z-40 hidden min-[1600px]:block"
      style={{
        opacity: pastHero ? 1 : 0,
        transform: `translateY(${pastHero ? bob : 40}px) rotate(${tilt}deg) scale(0.8)`,
        transformOrigin: 'bottom right',
        transition: 'opacity 400ms ease, transform 300ms ease-out',
        filter: 'drop-shadow(0 0 18px rgba(46,107,255,0.55))',
      }}
    >
      <CuteRobot blink={blink} wave={wave} />
    </div>
  );
}
